import { useState } from 'react';
import { Plus, ArrowUp, ArrowDown, Pencil, Archive, X, ImagePlus, Monitor, Smartphone } from 'lucide-react';
import { getAllProducts, getCategory } from '../../../data/catalog';
import { useSiteConfig, saveSiteConfig, fileToDataUrl } from '../../../utils/siteConfig';
import { useQueue } from '../../../context/QueueContext';
import DestinationPicker from './DestinationPicker';

const blankDraft = () => ({
  id: null,
  title: '',
  text: '',
  buttonText: 'EXPLORAR TECIDOS',
  destType: 'todos',
  destValue: '',
  imageDesktop: null,
  imageMobile: null,
  active: true,
  archived: false,
});

function destLabel(b) {
  if (b.destType === 'categoria') return `Categoria: ${getCategory(b.destValue)?.name || b.destValue}`;
  if (b.destType === 'tecido') return `Tecido: ${getAllProducts().find((p) => p.slug === b.destValue)?.name || b.destValue}`;
  if (b.destType === 'loja') return 'Nossa Loja';
  return 'Todos os Tecidos';
}

function BannerMock({ b, mode }) {
  const img = mode === 'mobile' ? b.imageMobile || b.imageDesktop : b.imageDesktop;
  const inner = (
    <>
      <p className="whitespace-pre-line font-serif text-2xl leading-tight text-navy">{b.title || 'Título do banner'}</p>
      {b.text && <p className="mt-2 text-sm leading-relaxed text-foreground/70">{b.text}</p>}
      {b.buttonText && (
        <span className="mt-3 inline-block rounded-full bg-navy px-5 py-2.5 text-xs font-bold tracking-wide text-white">
          {b.buttonText}
        </span>
      )}
    </>
  );
  if (mode === 'mobile') {
    return (
      <div className="w-[300px] overflow-hidden rounded-2xl bg-[#F4EFE8] text-left" data-testid="pers-banner-preview-mobile-frame">
        {img && <img src={img} alt="" className="h-40 w-full object-cover" />}
        <div className="p-4">{inner}</div>
      </div>
    );
  }
  return (
    <div className="grid w-full max-w-3xl grid-cols-2 items-center gap-6 rounded-2xl bg-[#F4EFE8] p-6 text-left" data-testid="pers-banner-preview-desktop-frame">
      <div>{inner}</div>
      {img && <img src={img} alt="" className="h-56 w-full rounded-xl object-cover" />}
    </div>
  );
}

export default function BannersSection({ flash }) {
  const { user } = useQueue();
  const cfg = useSiteConfig();
  const [draft, setDraft] = useState(null);
  const [previewMode, setPreviewMode] = useState(null);

  const banners = [...cfg.banners].sort((a, b) => (a.order || 0) - (b.order || 0));

  const persist = (list, action) => saveSiteConfig({ banners: list }, user, action);

  const saveBanner = async () => {
    const list = [...cfg.banners];
    try {
      if (draft.id) {
        const idx = list.findIndex((x) => x.id === draft.id);
        list[idx] = { ...list[idx], ...draft };
        await persist(list, `Editou o banner "${draft.title}".`);
      } else {
        const order = list.reduce((m, x) => Math.max(m, x.order || 0), 0) + 1;
        list.push({ ...draft, id: `b${Date.now()}`, order });
        await persist(list, `Criou o banner "${draft.title}".`);
      }
      setDraft(null);
      flash('Banner salvo.');
    } catch (e) {
      flash(e.message || 'Falha ao salvar o banner.');
    }
  };

  const toggle = async (b) => {
    const list = cfg.banners.map((x) => (x.id === b.id ? { ...x, active: !x.active } : x));
    try {
      await persist(list, `${b.active ? 'Desativou' : 'Ativou'} o banner "${b.title}".`);
      flash(b.active ? 'Banner desativado.' : 'Banner ativado.');
    } catch (e) {
      flash(e.message || 'Falha ao atualizar o banner.');
    }
  };

  const archive = async (b) => {
    const list = cfg.banners.map((x) => (x.id === b.id ? { ...x, archived: true, active: false } : x));
    try {
      await persist(list, `Arquivou o banner "${b.title}".`);
      flash('Banner arquivado.');
    } catch (e) {
      flash(e.message || 'Falha ao arquivar o banner.');
    }
  };

  const move = async (b, dir) => {
    const list = [...banners];
    const i = list.findIndex((x) => x.id === b.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const tmp = list[i].order;
    list[i] = { ...list[i], order: list[j].order };
    list[j] = { ...list[j], order: tmp };
    try {
      await persist(list, 'Alterou a ordem dos banners da Home.');
    } catch (e) {
      flash(e.message || 'Falha ao reordenar os banners.');
    }
  };

  const upload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 1600);
    setDraft((d) => ({ ...d, [field]: dataUrl }));
    e.target.value = '';
  };

  const inputCls = 'h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';
  const canSave = draft && draft.title.trim() && draft.imageDesktop;

  return (
    <section className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="pers-banners-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-navy">Banners da Home</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Banners ativos formam o carrossel principal. Imagem mobile é independente; se vazia, usa a de desktop.
          </p>
        </div>
        {!draft && (
          <button
            type="button"
            onClick={() => setDraft(blankDraft())}
            data-testid="pers-banner-new"
            className="flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
          >
            <Plus className="h-4 w-4" /> NOVO BANNER
          </button>
        )}
      </div>

      {draft && (
        <div className="mt-4 rounded-2xl border border-navy/15 bg-navy-light/30 p-4" data-testid="pers-banner-editor">
          <p className="text-sm font-semibold text-navy">{draft.id ? 'Editar banner' : 'Novo banner'}</p>
          <div className="mt-3 space-y-3">
            <div>
              <label htmlFor="pers-banner-title" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Título</label>
              <input id="pers-banner-title" data-testid="pers-banner-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={`mt-1 ${inputCls}`} placeholder="Tecidos escolhidos para seus projetos" />
            </div>
            <div>
              <label htmlFor="pers-banner-text" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Texto complementar</label>
              <textarea id="pers-banner-text" data-testid="pers-banner-text-input" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} rows={2} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" placeholder="Encontre o tecido que procura..." />
            </div>
            <div>
              <label htmlFor="pers-banner-button" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Texto do botão</label>
              <input id="pers-banner-button" data-testid="pers-banner-button-input" value={draft.buttonText} onChange={(e) => setDraft({ ...draft, buttonText: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Destino do botão</p>
              <div className="mt-1">
                <DestinationPicker testid="banner-dest" destType={draft.destType} destValue={draft.destValue} onChange={(t, v) => setDraft({ ...draft, destType: t, destValue: v })} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-navy/10 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Imagem desktop *</p>
                {draft.imageDesktop && <img src={draft.imageDesktop} alt="" className="mt-2 h-24 w-full rounded-lg object-cover" data-testid="pers-banner-img-desktop-preview" />}
                <label htmlFor="pers-banner-img-desktop" data-testid="pers-banner-img-desktop-label" className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-navy/25 px-3 py-3 text-xs font-semibold text-navy hover:bg-navy-light">
                  <ImagePlus className="h-4 w-4" /> {draft.imageDesktop ? 'SUBSTITUIR' : 'ENVIAR'}
                </label>
                <input id="pers-banner-img-desktop" data-testid="pers-banner-img-desktop-input" type="file" accept="image/*" className="sr-only" onChange={(e) => upload(e, 'imageDesktop')} />
              </div>
              <div className="rounded-xl border border-navy/10 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Imagem mobile (opcional)</p>
                {draft.imageMobile && <img src={draft.imageMobile} alt="" className="mt-2 h-24 w-full rounded-lg object-cover" data-testid="pers-banner-img-mobile-preview" />}
                <label htmlFor="pers-banner-img-mobile" data-testid="pers-banner-img-mobile-label" className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-navy/25 px-3 py-3 text-xs font-semibold text-navy hover:bg-navy-light">
                  <ImagePlus className="h-4 w-4" /> {draft.imageMobile ? 'SUBSTITUIR' : 'ENVIAR'}
                </label>
                <input id="pers-banner-img-mobile" data-testid="pers-banner-img-mobile-input" type="file" accept="image/*" className="sr-only" onChange={(e) => upload(e, 'imageMobile')} />
                {!draft.imageMobile && <p className="mt-1 text-[11px] text-muted-foreground">Sem imagem mobile, a de desktop será usada.</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              <button type="button" onClick={() => setPreviewMode('desktop')} disabled={!draft.imageDesktop} data-testid="pers-banner-preview-desktop" className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-4 py-2.5 text-xs font-semibold text-navy hover:bg-white disabled:opacity-40">
                <Monitor className="h-4 w-4" /> VISUALIZAR DESKTOP
              </button>
              <button type="button" onClick={() => setPreviewMode('mobile')} disabled={!draft.imageDesktop} data-testid="pers-banner-preview-mobile" className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-4 py-2.5 text-xs font-semibold text-navy hover:bg-white disabled:opacity-40">
                <Smartphone className="h-4 w-4" /> VISUALIZAR MOBILE
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:flex">
              <button type="button" onClick={() => setDraft(null)} data-testid="pers-banner-cancel" className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-5 py-3 text-sm font-semibold text-navy hover:bg-white">
                <X className="h-4 w-4" /> CANCELAR
              </button>
              <button type="button" onClick={saveBanner} disabled={!canSave} data-testid="pers-banner-save" className="flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-navy-hover disabled:opacity-40">
                SALVAR BANNER
              </button>
            </div>
            {!canSave && <p className="text-xs text-amber-700" data-testid="pers-banner-required-note">Título e imagem desktop são obrigatórios.</p>}
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3" data-testid="pers-banners-list">
        {banners.map((b, i) => (
          <div key={b.id} className={`rounded-2xl border p-3 ${b.archived ? 'border-navy/10 bg-navy-light/30 opacity-70' : 'border-navy/10 bg-white'}`} data-testid={`pers-banner-card-${b.id}`}>
            <div className="flex gap-3">
              {b.imageDesktop && <img src={b.imageMobile || b.imageDesktop} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium leading-tight">{b.title}</p>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{destLabel(b)} → {b.buttonText}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${b.active ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid={`pers-banner-status-${b.id}`}>
                    {b.active ? 'Ativo' : 'Inativo'}
                  </span>
                  {b.archived && <span className="rounded-full bg-gray-200 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">Arquivado</span>}
                  {b.imageMobile && <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-800">Imagem mobile própria</span>}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => move(b, -1)} disabled={i === 0} aria-label="Mover para cima" data-testid={`pers-banner-up-${b.id}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-navy/20 text-navy hover:bg-navy-light disabled:opacity-30">
                <ArrowUp className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => move(b, 1)} disabled={i === banners.length - 1} aria-label="Mover para baixo" data-testid={`pers-banner-down-${b.id}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-navy/20 text-navy hover:bg-navy-light disabled:opacity-30">
                <ArrowDown className="h-4 w-4" />
              </button>
              {!b.archived && (
                <>
                  <button type="button" onClick={() => setDraft({ ...b })} data-testid={`pers-banner-edit-${b.id}`} className="flex h-10 items-center gap-2 rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light">
                    <Pencil className="h-4 w-4" /> EDITAR
                  </button>
                  <button type="button" onClick={() => toggle(b)} data-testid={`pers-banner-toggle-${b.id}`} className="flex h-10 items-center rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light">
                    {b.active ? 'DESATIVAR' : 'ATIVAR'}
                  </button>
                  <button type="button" onClick={() => archive(b)} data-testid={`pers-banner-archive-${b.id}`} className="flex h-10 items-center gap-2 rounded-xl border border-red-300 px-4 text-xs font-semibold text-red-700 hover:bg-red-50">
                    <Archive className="h-4 w-4" /> ARQUIVAR
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {banners.length === 0 && (
          <p className="rounded-2xl border border-dashed border-navy/20 p-8 text-center text-sm text-muted-foreground" data-testid="pers-banners-empty">
            Nenhum banner cadastrado. A Home exibe o conteúdo principal editável na aba “Home”.
          </p>
        )}
      </div>

      {previewMode && draft && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="pers-banner-preview-modal" onClick={() => setPreviewMode(null)}>
          <div className="max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <BannerMock b={draft} mode={previewMode} />
            <button type="button" onClick={() => setPreviewMode(null)} data-testid="pers-banner-preview-close" className="mx-auto mt-3 flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-navy">
              <X className="h-4 w-4" /> FECHAR PRÉVIA
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
